import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
    DEFAULT_MESSAGE_PRIORITY,
    DEFAULT_MESSAGE_PRIORITY_VALUE,
    MESSAGE_PRIORITY_VALUE,
} from 'src/constants/messagePriority';
import useAuth from 'src/hooks/useAuth';
import useViewMessage from 'src/hooks/useViewMessage';
import useCreateMessageApi from 'src/services/messages_services/useCreateMessageApi';
import { toApiError } from 'src/utils/errorHandling';
import { sanitizePlainText } from 'src/utils/sanitization';
import { ComposeFormData } from './ComposeMessageForm';

interface UseComposeMessageLogicProps {
  initialRecipient?: string;
  initialSubject?: string;
  initialMessage?: string;
  replyToMessage?: {
    messageId: string;
    subject: string;
    sender: string;
  };
  onSuccess?: () => void;
}

export const useComposeMessageLogic = ({
  initialRecipient = '',
  initialSubject = '',
  initialMessage = '',
  replyToMessage,
  onSuccess
}: UseComposeMessageLogicProps) => {
  const { t } = useTranslation();
  const { clientId: authClientId } = useAuth();
  
  // Access unified context to refresh the grid after successful message send
  const { refreshTable } = useViewMessage();
  
  // Use the new createMessage API hook
  const { createMessage, isCreatingMessage } = useCreateMessageApi();
  
  const [formData, setFormData] = useState<ComposeFormData>({
    recipient: initialRecipient,
    subject: replyToMessage ? `Re: ${replyToMessage.subject}` : initialSubject,
    message: initialMessage,
    priority: DEFAULT_MESSAGE_PRIORITY,
    attachments: [] as File[]
  });
  
  const [selectedRecipients, setSelectedRecipients] = useState<string[]>([]);
  const [errorState, setErrorState] = useState<{ hasError: boolean; message: string }>({
    hasError: false,
    message: ''
  });
  const [isMessageSent, setIsMessageSent] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [showStatus, setShowStatus] = useState(false);

  // Validation function to check if all required fields have values
  const isFormValid = useMemo(() => {
    const hasRecipients = selectedRecipients.length > 0 || formData.recipient.trim().length > 0;
    const hasSubject = formData.subject.trim().length > 0;
    const hasMessage = formData.message.trim().length > 0;
    return hasRecipients && hasSubject && hasMessage;
  }, [selectedRecipients, formData.recipient, formData.subject, formData.message]);

  const handleInputChange = useCallback((field: string, value: string) => {
    if (errorState.hasError) return; // Prevent changes when in error state
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, [errorState.hasError]);

  const handleRecipientChange = useCallback((recipientId: string, isChecked: boolean) => {
    if (errorState.hasError) return; // Prevent changes when in error state
    
    if (isChecked) {
      setSelectedRecipients(prev => [...prev, recipientId]);
    } else {
      setSelectedRecipients(prev => prev.filter(id => id !== recipientId));
    }
  }, [errorState.hasError]);

  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (errorState.hasError) return; // Prevent changes when in error state
    
    const files = Array.from(event.target.files || []);
    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...files]
    }));
  }, [errorState.hasError]);

  const removeAttachment = useCallback((index: number) => {
    if (errorState.hasError) return; // Prevent changes when in error state
    
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  }, [errorState.hasError]);

  const clearError = useCallback(() => {
    setErrorState({ hasError: false, message: '' });
    setIsMessageSent(false);
    setStatusMessage('');
    setShowStatus(false);
  }, []);

  const resetForm = useCallback(() => {
    setFormData({
      recipient: '',
      subject: replyToMessage ? `Re: ${replyToMessage.subject}` : '',
      message: '',
      priority: DEFAULT_MESSAGE_PRIORITY,
      attachments: []
    });
    setSelectedRecipients([]);
    setErrorState({ hasError: false, message: '' });
    setStatusMessage('');
    setShowStatus(false);
    setIsMessageSent(false);
  }, [replyToMessage]);

  const handleSend = useCallback(async () => {
    // Prevent multiple simultaneous send attempts
    if (isSending || isCreatingMessage) {
      console.warn('Message send already in progress, ignoring duplicate request');
      return;
    }

    // Clear any previous error state
    clearError();
    
    // Validate required fields based on current validation logic
    const hasRecipients = selectedRecipients.length > 0 || formData.recipient.trim().length > 0;
    const hasSubject = formData.subject.trim().length > 0;
    const hasMessage = formData.message.trim().length > 0;
    
    if (!hasRecipients || !hasSubject || !hasMessage) {
      console.warn('Validation failed: Missing required fields');
      toast.error(t('Please provide values for all required fields'));
      return;
    }

    setIsSending(true);

    try {
      // Get clientId from JWT token
      const clientId = authClientId ? parseInt(authClientId, 10) : 0;
      
      if (!clientId || isNaN(clientId)) {
        console.error('No valid clientId available for sending message');
        toast.error(t('Unable to send message: No valid client selected'));
        return;
      }

      // Prepare request body for CreateMessage API
      const requestBody = {
        Subject: sanitizePlainText(formData.subject.trim()),
        Body: sanitizePlainText(formData.message.trim()),
        SenderClientId: Number(clientId), // decimal
        ProxyClientId: Number(clientId), // decimal
        Priority: MESSAGE_PRIORITY_VALUE[formData.priority as keyof typeof MESSAGE_PRIORITY_VALUE] || DEFAULT_MESSAGE_PRIORITY_VALUE, // int
        IsReply: Boolean(replyToMessage),
        RecipientClientIds: selectedRecipients.map(id => Number(id)) // list of decimals
      };

      await createMessage(requestBody);
      
      toast.success(t('Message sent successfully'));
      
      // Set sent state to disable send button and stay on screen
      setIsMessageSent(true);
      
      setStatusMessage(`${t('Success')}: ${t('Message sent successfully')}`);
      setShowStatus(true);
      
      // Refresh the grid to show the sent message
      refreshTable();
      
      // Reset form to clear all fields for next compose
      resetForm();
      
      // Call success callback (closes modal or navigates back in standalone mode)
      if (onSuccess) {
        onSuccess();
      }
      
    } catch (error: unknown) {
      const apiError = toApiError(error);
      console.error('Failed to send message:', apiError);
      console.error('Error details:', {
        message: apiError.message,
        status: apiError.response?.status,
        statusText: apiError.response?.statusText,
        data: apiError.response?.data
      });
      
      const errorMessage = apiError.response?.data?.message || apiError.message || t('Failed to send message');
      toast.error(`${t('Error sending message')}: ${errorMessage}`);
      
      // Set error state for modal-specific error handling
      setErrorState({
        hasError: true,
        message: errorMessage
      });
      
      // Show failure status
      setStatusMessage(`${t('Failed')}: ${errorMessage}`);
      setShowStatus(true);
    } finally {
      setIsSending(false);
    }
  }, [formData, selectedRecipients, refreshTable, t, authClientId, clearError, setErrorState, setStatusMessage, setShowStatus, setIsMessageSent, createMessage, isSending, isCreatingMessage, resetForm, onSuccess, replyToMessage]);

  return {
    formData,
    selectedRecipients,
    errorState,
    isFormValid,
    isMessageSent,
    isSending,
    statusMessage,
    showStatus,
    handleInputChange,
    handleRecipientChange,
    handleFileUpload,
    removeAttachment,
    handleSend,
    resetForm,
    clearError
  };
};